import moment from 'moment';
import { calculatePercentageDifference, fillDateSeries } from '../../../Campaigns/campaigndetails/utils';

// =============================================================================
// Editorial date-range comparison helpers (GA-style).
//
// The Editorial details page already holds the FULL lifetime by-date data on
// the client (EDITORIAL query returns editorial.byDate / sessionsByDate in
// full; Page Views / Sign Ups load the full S3 report). Comparison is therefore
// a purely client-side computation: run the tab's existing filterByDate reduce
// a second time against a computed comparison range, then diff the totals and
// overlay the series. These helpers own only the range math, the index
// alignment, and the percent-delta shaping — the per-tab reduce is reused.
// =============================================================================

export const COMPARISON_MODES = {
  OFF: 'off',
  PREVIOUS: 'previous',
  PREVIOUS_MONTH: 'previousMonth',
  CUSTOM: 'custom',
};

const DAY_FORMAT = 'YYYY-MM-DD';
const day = (d) => moment(d).format(DAY_FORMAT);

// A range is a "whole calendar month" when it starts on the 1st and ends on the
// last day of the same month — lets MoM snap to real month boundaries.
const isWholeMonth = (start, end) =>
  start.isSame(end, 'month') &&
  start.isSame(end, 'year') &&
  start.isSame(start.clone().startOf('month'), 'day') &&
  end.isSame(end.clone().endOf('month'), 'day');

// Given the current range + mode, return the comparison range [start, end] as
// moments, or null when there is nothing to compare (off / no bounded range).
export const getComparisonRange = (mode, currentRange, customRange) => {
  if (!mode || mode === COMPARISON_MODES.OFF) return null;
  if (!currentRange || !currentRange[0] || !currentRange[1]) return null;

  const start = moment(currentRange[0]);
  const end = moment(currentRange[1]);
  if (!start.isValid() || !end.isValid()) return null;

  if (mode === COMPARISON_MODES.CUSTOM) {
    if (!customRange || !customRange[0] || !customRange[1]) return null;
    const cStart = moment(customRange[0]);
    const cEnd = moment(customRange[1]);
    return cStart.isValid() && cEnd.isValid() ? [cStart, cEnd] : null;
  }

  if (mode === COMPARISON_MODES.PREVIOUS_MONTH) {
    // Whole-month selections compare against the previous whole month so
    // Feb-vs-Jan aligns to real month boundaries regardless of month length.
    if (isWholeMonth(start, end)) {
      const cmpStart = start.clone().subtract(1, 'month').startOf('month');
      return [cmpStart, cmpStart.clone().endOf('month')];
    }
    // Arbitrary range: shift a month back (moment clamps short months, e.g.
    // Mar 31 -> Feb 28/29).
    return [start.clone().subtract(1, 'month'), end.clone().subtract(1, 'month')];
  }

  // PREVIOUS (default): a distinct, same-length window immediately before the
  // current one. The +1 guarantees non-overlap (a 1-day range compares to the
  // day before, never to itself). Mirrors campaign processPercentage.
  const len = end.diff(start, 'days') + 1;
  return [start.clone().subtract(len, 'days'), end.clone().subtract(len, 'days')];
};

// Build the chart series: current rows carry their metrics under the normal
// keys (so the existing metric dropdowns keep working) plus a `prev_<metric>`
// value taken from the comparison window aligned by DAY-INDEX (GA semantics:
// day 1 vs day 1, not calendar date). Both windows are gap-filled first so the
// index alignment is honest even when either window has missing days.
// `currentDate` / `previousDate` carry the two real dates for the tooltip.
export const buildComparisonSeries = (
  currentRows,
  previousRows,
  currentRange,
  previousRange,
  metricKeys,
  xAxisKey = 'xAxis'
) => {
  if (!currentRange || !previousRange) return currentRows || [];

  const opts = { xAxisKey, zeroFields: metricKeys };
  const current = fillDateSeries(
    currentRows || [],
    day(currentRange[0]),
    day(currentRange[1]),
    opts
  );
  const previous = fillDateSeries(
    previousRows || [],
    day(previousRange[0]),
    day(previousRange[1]),
    opts
  );

  // Keep every current day visible; overlay the comparison value only where the
  // previous window has a matching index (unequal custom windows leave a gap).
  return current.map((cur, i) => {
    const prev = previous[i];
    const row = {
      ...cur,
      currentDate: cur[xAxisKey],
      previousDate: prev ? prev[xAxisKey] : null,
    };
    metricKeys.forEach((key) => {
      row[`prev_${key}`] = prev ? Number(prev[key]) || 0 : null;
    });
    return row;
  });
};

// Percent change per metric, current vs comparison totals, shaped for DeltaChip
// ({ value, prev, ranges }). A zero previous value yields value:null so the
// chip renders nothing rather than Infinity.
export const computeDeltas = (currentTotals, previousTotals, metricKeys, comparisonRange) => {
  const ranges = comparisonRange
    ? `${moment(comparisonRange[0]).format('MMM DD YYYY')} – ${moment(comparisonRange[1]).format(
        'MMM DD YYYY'
      )}`
    : '';

  return metricKeys.reduce((acc, key) => {
    const current = Number(currentTotals && currentTotals[key]) || 0;
    const prev = Number(previousTotals && previousTotals[key]) || 0;
    acc[key] = {
      // calculatePercentageDifference returns a formatted string for positive
      // deltas; normalize to a number so the value type is consistent.
      value: prev === 0 ? null : Number(calculatePercentageDifference(current, prev)),
      prev,
      ranges,
    };
    return acc;
  }, {});
};
